import { cloneElement, isValidElement, useEffect, useId, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { computePosition, type Side } from './position.ts';
import './Tooltip.css';

export interface TooltipProps {
  content: ReactNode;
  children: ReactElement<{ 'aria-describedby'?: string }>;
  side?: Side;
  delayMs?: number;
  disabled?: boolean;
  /** Link the tooltip via aria-describedby. Off when the text already is the child's accessible name. */
  describeChild?: boolean;
}

/** Hover/focus tooltip. Never shown for touch input; Esc hides it. */
export function Tooltip({ content, children, side = 'top', delayMs = 350, disabled = false, describeChild = true }: TooltipProps) {
  const id = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const show = (immediate: boolean) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(true), immediate ? 0 : delayMs);
  };
  const hide = () => {
    window.clearTimeout(timer.current);
    setOpen(false);
    setPos(null);
  };

  useEffect(() => () => window.clearTimeout(timer.current), []);

  useLayoutEffect(() => {
    if (!open) return;
    const anchor = anchorRef.current?.firstElementChild;
    const tip = tipRef.current;
    if (!anchor || !tip) return;
    const r = computePosition(anchor.getBoundingClientRect(), { width: tip.offsetWidth, height: tip.offsetHeight }, side, 'center', 8);
    setPos({ top: r.top, left: r.left });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };
    const onScroll = () => hide();
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
    // `hide` only touches refs and setters; re-run when visibility or side changes.
  }, [open, side]);

  if (disabled || content === null || content === undefined || content === '') return children;

  const child =
    describeChild && isValidElement(children)
      ? cloneElement(children, {
          'aria-describedby': [children.props['aria-describedby'], open ? id : null].filter(Boolean).join(' ') || undefined,
        })
      : children;

  return (
    <>
      <span
        ref={anchorRef}
        className="tooltip-anchor"
        onPointerEnter={(e) => {
          if (e.pointerType !== 'touch') show(false);
        }}
        onPointerLeave={hide}
        onPointerDown={hide}
        onFocusCapture={(e) => {
          const target = e.target as HTMLElement;
          try {
            if (target.matches(':focus-visible')) show(true);
          } catch {
            show(true);
          }
        }}
        onBlurCapture={hide}
      >
        {child}
      </span>
      {open &&
        createPortal(
          <div
            ref={tipRef}
            id={id}
            role="tooltip"
            className="tooltip"
            style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
